import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  commentsIcon,
  heartIcon,
  shareIcon,
  LineChart,
  BarChart,
  MultipleLineChart,
  AgeChart,
  Toast,
  InfluencerRating,
} from "../../Components";
import {
  commonapiService,
  reportService,
  jobCampaignsService,
  offerService,
} from "../../Services";

import { utils } from "../../Helpers/utils";
import { ArrowLeft, FileIcon, ChevronDown, ChevronUp, Dot } from "lucide-react";

export default function ReportInsight(props) {
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const source = queryParams.get("source");
  const [reportId, setReportId] = useState(id || props.modalData?.id);
  const [reportData, setReportData] = useState(props.modalData);
  const [platformToShow, setPlatformToShow] = useState(
    props.platformToShow || ""
  );
  const [toastList, setToastList] = useState([]);
  const [graphData, setGraphData] = useState({});
  const [isGraphDataLoaded, setIsGraphDataLoaded] = useState(false);
  const [isReportLoaded, setIsReportLoaded] = useState(!!props.modalData);
  const [influencerData, setInfluencerData] = useState({});
  const [campaignInformation, setCampaignInformation] = useState({});
  const [offerInformation, setOfferInformation] = useState({});
  const [statsError, setStatsError] = useState(null);
  const [accordionStates, setAccordionStates] = useState({
    campaign: false,
    offer: false
  });


  const toggleAccordion = (section) => {
    setAccordionStates(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const addToast = (data) => {
    setToastList((prev) => [
      ...prev,
      { ...data, id: `${Date.now()}-${prev.length}` },
    ]);
  };

  useEffect(() => {
    if (id) {
      setReportId(id);
    }
  }, [id]);

  const getPlatformIcon = (platformCode) => {
    const id = platformCode?.toString();

    switch (id) {
      case "Instagram":
      case "IG":
        return (
          <div className="flex gap-2">
            <img
              src="/images/instagram.svg"
              alt="Instagram"
              width="24"
              height="24"
            />
          </div>
        );
      case "Tiktok":
      case "TT":
        return (
          <div className="flex gap-2">
            <img
              src="/images/tiktok-logo.svg"
              alt="TikTok"
              width="24"
              height="24"
            />
          </div>
        );
      case "Facebook":
      case "FB":
        return (
          <div className="flex gap-2">
            <img
              src="/images/facebook.svg"
              alt="Facebook"
              width="24"
              height="24"
            />
          </div>
        );
      case "Youtube":
      case "YT":

        return (
          <div className="flex gap-2">
            <img
              src="/images/youtube.svg"
              alt="Facebook"
              width="24"
              height="24"
            />
          </div>
        );
      default:
        return <span>{platformCode || "NA"}</span>;
    }
  };

  useEffect(() => {
    async function getInitialData() {
      try {
        let currentReportData = props.modalData;
        let pToShow = props.platformToShow;

        if (!currentReportData && reportId) {
          let res;
          if (source === "campaign") {
            res = await jobCampaignsService.getCampaignById(reportId);
          } else if (source === "campaignOffer" || source === "directOffer") {
            res = await offerService.getOfferDetailsByOfferId(reportId);
          } else {
            res = await reportService.getReport(reportId);
          }

          currentReportData = Array.isArray(res.data) ? res.data[0] : res.data;

          if (res.success === false) {
            addToast({
              error: true,
              msg: res.message || "Failed to load report data",
            });
            setTimeout(() => navigate("/brand/reports"), 2000);
            return;
          }

          setReportData(currentReportData);

          const platformRes = await commonapiService.getConfigData();
          const platformDetails = platformRes.socialMedia;
          const platformUsed = platformDetails.find(
            (p) =>
              p.id === currentReportData.platform_id ||
              p.code === currentReportData.social_media_code
          );
          pToShow = platformUsed?.name || "";
          setPlatformToShow(pToShow);
          setIsReportLoaded(true);
          props.onHeaderTitleChange &&
            props.onHeaderTitleChange(
              currentReportData.title ||
              currentReportData.description ||
              "Report Details"
            );
        } else if (currentReportData) {
          props.onHeaderTitleChange &&
            props.onHeaderTitleChange(
              currentReportData.title || "Report Details"
            );
        }

        if (reportId) {
          try {
            let statsRes;
            if (source === "campaign") {
              statsRes = await reportService.getCampaignReportStats(reportId);
            } else if (source === "campaignOffer" || source === "directOffer") {
              statsRes = await reportService.getOfferReportStats(reportId);
            } else {
              statsRes = await reportService.getReportStats(reportId);
            }

            console.log("🚀 ~ getInitialData ~ statsRes:", statsRes);

            if (statsRes.success === true && statsRes.data !== null) {
              const transformedData = transformStatsData(statsRes.data);
              setGraphData(transformedData);
              setInfluencerData(statsRes.data?.report_info || {});
              setCampaignInformation(statsRes.data?.cmp_info || {});
              setOfferInformation(statsRes.data?.offer_info || {});
              setIsGraphDataLoaded(true);
              setStatsError(null);
            } else {
              if (statsRes.data === null || !statsRes.data) {
                setStatsError("No response found");
              } else {
                setStatsError(statsRes.message || "Failed to load report statistics");
              }

              setGraphData({});
              setIsGraphDataLoaded(false);
            }
          } catch (statsErr) {
            console.error("Stats error:", statsErr);
            setStatsError("No response found");
            setGraphData({});
            setIsGraphDataLoaded(false);
          }
        }
      } catch (err) {
        console.error("Initial data error:", err);
        addToast({
          error: true,
          msg: err.message || err,
        });
      }
    }
    getInitialData();
    // eslint-disable-next-line
  }, [reportId]);

  const transformStatsData = (apiData) => {
    if (!apiData || !apiData.stats) return {};

    const stats = apiData.stats;
    const transformed = {
      ...stats,
      label: stats.label || [],
      summary: {
        like: stats.like?.header || 0,
        comment: stats.comment?.header || 0,
        share: stats.share?.header || 0,
      },
      ageData: {
        header: {
          gender_m: stats.gender?.m || 0,
          gender_f: stats.gender?.f || 0,
          gender_u: stats.gender?.u || 0,
        },
        data: stats.age ? Object.entries(stats.age).map(([ageRange, value]) => ({
          label: ageRange,
          value: value
        })) : []
      }
    };

    return transformed;
  };

  const safeGet = (obj, path, defaultValue = 0) => {
    if (!obj) return defaultValue;
    try {
      return path.split('.').reduce((acc, part) => {
        if (acc === null || acc === undefined) return defaultValue;
        return acc[part];
      }, obj) ?? defaultValue;
    } catch {
      return defaultValue;
    }
  };

  const navigate = useNavigate();

  if (!isReportLoaded || !reportData) {
    return (
      <>
        <div className="flex items-center justify-center h-[400px]">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-500 font-medium">Loading report data...</p>
          </div>
        </div>
        <Toast list={toastList} setList={setToastList} />
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-center justify-between">
        <button
          onClick={() => {
            if (source === "campaign" || source === "campaignOffer") {
              navigate("/brand/jobCampaigns");
            } else if (source === "directOffer") {
              navigate("/brand/offer");
            } else {
              navigate("/brand/reports");
            }
          }}
          className="flex h-[38px] px-5 justify-center items-center rounded-full text-sm font-medium cursor-pointer transition-all duration-200 bg-gray-50 text-gray-600"
          style={{
            boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
          }}
        >
          <ArrowLeft strokeWidth={2.2} size={18} className="mr-2" />
          Back
        </button>
      </header>

      <div className="bg-white rounded-2xl border border-gray-100 p-6" style={{ boxShadow: "0px 5px 15px rgba(33, 48, 53, 0.1), 0px 1px 4px rgba(33, 48, 53, 0.1)" }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {reportData?.title && (
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1 uppercase tracking-wide">
                Title
              </p>
              <p className="text-base text-gray-900 bg-gray-50 p-3 rounded-lg">
                {reportData.title || "N/A"}
              </p>
            </div>
          )}

          {influencerData?.ReportItem && influencerData?.ReportItem?.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1 uppercase tracking-wide">
                Handle
              </p>
              <div className="text-base text-gray-900 bg-gray-50 p-3 rounded-lg">
                <ul className="space-y-1">
                  {influencerData?.ReportItem.map((item, index) => (
                    <li key={index} className="flex items-center">
                      {influencerData.ReportItem.length > 1 && <Dot />}
                      {item?.infHandle || "N/A"}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {influencerData?.ReportItem && influencerData?.ReportItem?.length > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1 uppercase tracking-wide">
                Hashtag
              </p>
              <div className="text-base text-gray-900 bg-gray-50 p-3 rounded-lg">
                <ul className="space-y-1">
                  {influencerData?.ReportItem.map((item, index) => (
                    <li key={index} className="flex items-center">
                      {influencerData.ReportItem.length > 1 && <Dot />}
                      {item?.hashtag || "N/A"}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {reportData?.token_used > 0 && (
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1 uppercase tracking-wide">
                Tokens Used
              </p>
              <p className="text-base text-gray-900 bg-gray-50 p-3 rounded-lg">
                {reportData?.token_used || "0"}
              </p>
            </div>
          )}

          {platformToShow && (
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1 uppercase tracking-wide">
                Platform
              </p>
              <p className="text-base text-gray-900 bg-gray-50 p-3 rounded-lg">
                {getPlatformIcon(platformToShow) || "N/A"}
              </p>
            </div>
          )}

          {reportData?.start_date && (
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1 uppercase tracking-wide">
                Timeframe
              </p>
              <p className="text-base text-gray-900 bg-gray-50 p-3 rounded-lg">
                {reportData.start_date ? new Date(reportData.start_date).toLocaleDateString("en-GB").replace(/\//g, "-") : "N/A"} - {reportData.end_date ? new Date(reportData.end_date).toLocaleDateString("en-GB").replace(/\//g, "-") : "N/A"}
              </p>
            </div>
          )}

          {reportData?.description && (
            <div>
              <p className="text-sm font-semibold text-gray-600 mb-1 uppercase tracking-wide">
                Description
              </p>
              <p className="text-base text-gray-900 bg-gray-50 p-3 rounded-lg">
                {
                  typeof reportData?.description === 'string' && !utils.testJSON(reportData?.description)
                    ? reportData?.description
                    : utils.draftJsFormatingForDetails({
                      data: reportData?.description,
                      view: "fullview",
                    })
                }
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Campaign, Offer, and Insights Sections */}
      <div className="campaignsInsight">
        {campaignInformation && Object.keys(campaignInformation).length > 0 && (
          <div className="campaignsInsight__insight">
            {/* Professional Card Container */}
            <div
              className="bg-white rounded-2xl border border-gray-200 overflow-hidden"
              style={{
                boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.08)"
              }}
            >
              {/* Subtle Top Accent */}
              <div
                className="h-1"
                style={{ background: "linear-gradient(90deg, rgb(94, 176, 90) 0%, rgb(54, 122, 64) 50%, rgb(16, 38, 32) 100%)" }}
              />

              <div className="p-6">
                {/* Header Inside Card - Clickable for Accordion */}
                <div
                  className="flex items-center justify-between cursor-pointer group"
                  onClick={() => toggleAccordion('campaign')}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                      style={{ background: "linear-gradient(135deg, rgb(94, 176, 90) 0%, rgb(54, 122, 64) 100%)" }}
                    >
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 tracking-tight">Campaign Information</h3>
                      <p className="text-xs text-gray-500">Complete campaign details and assets</p>
                    </div>
                  </div>
                  <div className="text-gray-400 group-hover:text-emerald-500 transition-colors">
                    {accordionStates.campaign ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                  </div>
                </div>

                {accordionStates.campaign && (
                  <div className="mt-6 animate-in fade-in slide-in-from-top-2 duration-300">

                    {/* Primary Info Grid - Clean & Minimal */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                      {campaignInformation?.title && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Title</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            {campaignInformation?.title || "N/A"}
                          </p>
                        </div>
                      )}

                      {campaignInformation?.brand_name && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Brand</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {campaignInformation?.brand_photo_url && (
                              <img
                                src={campaignInformation?.brand_photo_url}
                                alt="Brand Logo"
                                className="w-8 h-8 rounded-lg object-cover border border-gray-200"
                              />
                            )}
                            <p className="text-sm font-semibold text-gray-900">
                              {campaignInformation?.brand_name || "N/A"}
                            </p>
                          </div>
                        </div>
                      )}

                      {campaignInformation?.location_name && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Location</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            {campaignInformation?.location_name || "N/A"}
                          </p>
                        </div>
                      )}

                      {campaignInformation?.category_name && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Category</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            {campaignInformation?.category_name || "N/A"}
                          </p>
                        </div>
                      )}

                      {campaignInformation?.platform_code && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Platform</span>
                          </div>
                          <div className="flex items-center">
                            {getPlatformIcon(campaignInformation?.platform_code) || <span className="text-sm font-semibold text-gray-900">N/A</span>}
                          </div>
                        </div>
                      )}

                      {campaignInformation?.hashtag && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-gray-400 font-medium text-sm">#</span>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Hashtag</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900 break-all">
                            {campaignInformation?.hashtag || "N/A"}
                          </p>
                        </div>
                      )}

                      {campaignInformation?.reach_min && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Min Reach</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            {typeof campaignInformation?.reach_min === 'number'
                              ? campaignInformation.reach_min.toLocaleString()
                              : campaignInformation?.reach_min || "N/A"}
                          </p>
                        </div>
                      )}

                      {campaignInformation?.delivery_date && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Delivery Date</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            {campaignInformation?.delivery_date || "N/A"}
                          </p>
                        </div>
                      )}

                      {campaignInformation?.compensation && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Compensation</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            {campaignInformation?.compensation || "N/A"}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Full Width Text Sections - Clean Style */}
                    {(campaignInformation?.conv_url || campaignInformation?.delivery_info || campaignInformation?.additional_info || campaignInformation?.scope_of_work) && (
                      <div className="space-y-3 mb-6">
                        {campaignInformation?.conv_url && (
                          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                            <div className="flex items-center gap-2 mb-2">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Landing URL</span>
                            </div>
                            <a
                              href={campaignInformation?.conv_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium break-all hover:underline transition-colors"
                            >
                              {campaignInformation?.conv_url || "N/A"}
                            </a>
                          </div>
                        )}

                        {campaignInformation?.delivery_info && (
                          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                            <div className="flex items-center gap-2 mb-2">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                              </svg>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Delivery Information</span>
                            </div>
                            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                              {campaignInformation?.delivery_info || "N/A"}
                            </p>
                          </div>
                        )}

                        {campaignInformation?.additional_info && (
                          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                            <div className="flex items-center gap-2 mb-2">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Additional Information</span>
                            </div>
                            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                              {campaignInformation?.additional_info || "N/A"}
                            </p>
                          </div>
                        )}

                        {campaignInformation?.scope_of_work && (
                          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                            <div className="flex items-center gap-2 mb-2">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                              </svg>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Scope of Work</span>
                            </div>
                            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                              {campaignInformation?.scope_of_work || "N/A"}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Attached Files Section - Minimal */}
                    {campaignInformation?.files && campaignInformation?.files.length > 0 && (
                      <div className="pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-2 mb-4">
                          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                          </svg>
                          <span className="text-sm font-semibold text-gray-700">
                            Attached Files ({campaignInformation?.files.length})
                          </span>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                          {campaignInformation?.files.map((file, index) => {
                            const fileExtension = file.file_name
                              .split(".")
                              .pop()
                              .toLowerCase();
                            const isImage = [
                              "png",
                              "jpg",
                              "jpeg",
                              "gif",
                              "webp",
                              "bmp",
                            ].includes(fileExtension);
                            const isVideo = [
                              "mp4",
                              "avi",
                              "mov",
                              "wmv",
                              "flv",
                              "webm",
                              "mkv",
                            ].includes(fileExtension);

                            return (
                              <div
                                key={index}
                                className="group bg-gray-50 rounded-xl overflow-hidden border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all duration-200"
                              >
                                {/* File Preview */}
                                <div className="relative h-32 bg-gray-100 overflow-hidden">
                                  {isImage ? (
                                    <img
                                      src={file.file_url}
                                      alt={file.file_name}
                                      className="w-full h-full object-cover cursor-pointer group-hover:scale-105 transition-transform duration-300"
                                      onClick={() => window.open(file.file_url, "_blank")}
                                    />
                                  ) : isVideo ? (
                                    <video
                                      className="w-full h-full object-cover"
                                      controls
                                      controlsList="nodownload"
                                    >
                                      <source
                                        src={file.file_url}
                                        type={`video/${fileExtension}`}
                                      />
                                    </video>
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center bg-gray-50">
                                      <div className="text-center">
                                        <FileIcon fileName={file.file_name} className="w-10 h-10 mx-auto mb-1" />
                                        <span className="text-xs font-medium text-gray-500 uppercase">
                                          {fileExtension}
                                        </span>
                                      </div>
                                    </div>
                                  )}
                                </div>

                                {/* File Info */}
                                <div className="p-3">
                                  <p className="text-xs font-medium text-gray-700 truncate mb-2" title={file.file_name}>
                                    {file.file_name}
                                  </p>
                                  <a
                                    href={file.file_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
                                    style={{
                                      background: "linear-gradient(135deg, rgb(94, 176, 90) 0%, rgb(54, 122, 64) 100%)",
                                      color: "white"
                                    }}
                                  >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                    </svg>
                                    View
                                  </a>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {offerInformation && Object.keys(offerInformation).length > 0 && (
          <div className="campaignsInsight__insight">
            {/* Professional Card Container */}
            <div
              className="bg-white rounded-2xl border border-gray-200 overflow-hidden"
              style={{
                boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.08)"
              }}
            >
              {/* Subtle Top Accent */}
              <div
                className="h-1"
                style={{ background: "linear-gradient(90deg, rgb(94, 176, 90) 0%, rgb(54, 122, 64) 50%, rgb(16, 38, 32) 100%)" }}
              />

              <div className="p-6">
                {/* Header Inside Card - Clickable for Accordion */}
                <div
                  className="flex items-center justify-between cursor-pointer group"
                  onClick={() => toggleAccordion('offer')}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                      style={{ background: "linear-gradient(135deg, rgb(94, 176, 90) 0%, rgb(54, 122, 64) 100%)" }}
                    >
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 tracking-tight">Offer Information</h3>
                      <p className="text-xs text-gray-500">Complete offer details and terms</p>
                    </div>
                  </div>
                  <div className="text-gray-400 group-hover:text-emerald-500 transition-colors">
                    {accordionStates.offer ? <ChevronUp size={24} /> : <ChevronDown size={24} />}
                  </div>
                </div>

                {accordionStates.offer && (
                  <div className="mt-6 animate-in fade-in slide-in-from-top-2 duration-300">

                    {/* Primary Info Grid - Clean & Minimal */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                      {offerInformation?.to_displayname && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Name</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {offerInformation?.to_profile_img_url && (
                              <img
                                src={offerInformation?.to_profile_img_url}
                                alt="Profile"
                                className="w-8 h-8 rounded-lg object-cover border border-gray-200"
                              />
                            )}
                            <p className="text-sm font-semibold text-gray-900">
                              {offerInformation?.to_displayname || "N/A"}
                            </p>
                          </div>
                        </div>
                      )}

                      {offerInformation?.brand_name && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Brand Name</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {offerInformation?.brand_logo_url && (
                              <img
                                src={offerInformation?.brand_logo_url}
                                alt="Brand Logo"
                                className="w-8 h-8 rounded-lg object-cover border border-gray-200"
                              />
                            )}
                            <p className="text-sm font-semibold text-gray-900">
                              {offerInformation?.brand_name || "N/A"}
                            </p>
                          </div>
                        </div>
                      )}

                      {offerInformation?.from_displayname && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Display Name</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {offerInformation?.from_profile_img_url && (
                              <img
                                src={offerInformation?.from_profile_img_url}
                                alt="Profile"
                                className="w-8 h-8 rounded-lg object-cover border border-gray-200"
                              />
                            )}
                            <p className="text-sm font-semibold text-gray-900">
                              {offerInformation?.from_displayname || "N/A"}
                            </p>
                          </div>
                        </div>
                      )}

                      {offerInformation?.social_media_code && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Platform</span>
                          </div>
                          <div className="flex items-center">
                            {getPlatformIcon(offerInformation?.social_media_code) || <span className="text-sm font-semibold text-gray-900">N/A</span>}
                          </div>
                        </div>
                      )}

                      {offerInformation?.hashtag && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-gray-400 font-medium text-sm">#</span>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Hashtag</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900 break-all">
                            {offerInformation?.hashtag || "N/A"}
                          </p>
                        </div>
                      )}

                      {offerInformation?.to_followers > 0 && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Followers</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            {typeof offerInformation?.to_followers === 'number'
                              ? offerInformation.to_followers.toLocaleString()
                              : offerInformation?.to_followers || "N/A"}
                          </p>
                        </div>
                      )}

                      {offerInformation?.to_rating && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Rating</span>
                          </div>
                          <div className="text-sm font-semibold text-gray-900">
                            <InfluencerRating infrating={offerInformation?.to_rating || "N/A"} />
                          </div>
                        </div>
                      )}

                      {offerInformation?.status && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Status</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            {offerInformation?.status || "N/A"}
                          </p>
                        </div>
                      )}

                      {offerInformation?.product_name && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Product Name</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            {offerInformation?.product_name || "N/A"}
                          </p>
                        </div>
                      )}

                      {offerInformation?.quantity && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Quantity</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            {offerInformation?.quantity || "N/A"}
                          </p>
                        </div>
                      )}

                      {offerInformation?.price && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Price</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            {offerInformation?.price || "N/A"}
                          </p>
                        </div>
                      )}

                      {offerInformation?.offer_type && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Offer Type</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            {offerInformation?.offer_type || "N/A"}
                          </p>
                        </div>
                      )}

                      {offerInformation?.to_engagement_rate && (
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                            <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Engagement Rate</span>
                          </div>
                          <p className="text-sm font-semibold text-gray-900">
                            {offerInformation?.to_engagement_rate || "N/A"}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Full Width Text Sections - Clean Style */}
                    {(offerInformation?.brand_website || offerInformation?.description || offerInformation?.brand_bio) && (
                      <div className="space-y-3">
                        {offerInformation?.brand_website && (
                          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                            <div className="flex items-center gap-2 mb-2">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                              </svg>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Website</span>
                            </div>
                            <a
                              href={offerInformation?.brand_website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium break-all hover:underline transition-colors"
                            >
                              {offerInformation?.brand_website || "N/A"}
                            </a>
                          </div>
                        )}

                        {offerInformation?.description && (
                          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                            <div className="flex items-center gap-2 mb-2">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                              </svg>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Description</span>
                            </div>
                            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                              {offerInformation?.description || "N/A"}
                            </p>
                          </div>
                        )}

                        {offerInformation?.brand_bio && (
                          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                            <div className="flex items-center gap-2 mb-2">
                              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Bio</span>
                            </div>
                            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                              {offerInformation?.brand_bio || "N/A"}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {isGraphDataLoaded && graphData && Object.keys(graphData).length > 0 ? (
          <div className="">
            {/* Insights Section */}
            <div
              className="bg-white rounded-2xl border border-gray-200 overflow-hidden mb-4"
              style={{ boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.08)" }}
            >
              {/* Subtle Top Accent */}
              <div
                className="h-1"
                style={{ background: "linear-gradient(90deg, rgb(94, 176, 90) 0%, rgb(54, 122, 64) 50%, rgb(16, 38, 32) 100%)" }}
              />

              <div className="p-6">
                {/* Header Inside Card - Clickable for Accordion */}
                <div
                  className="flex items-center justify-between cursor-pointer group"
                  onClick={() => toggleAccordion('insights')}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                      style={{ background: "linear-gradient(135deg, rgb(94, 176, 90) 0%, rgb(54, 122, 64) 100%)" }}
                    >
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 tracking-tight">Insights</h3>
                      <p className="text-xs text-gray-500">Performance metrics at a glance</p>
                    </div>
                  </div>

                </div>

                <div className="mt-6 animate-in fade-in slide-in-from-top-2 duration-300">

                  {/* At a Glance Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center border border-gray-100">
                          {heartIcon}
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Likes</p>
                          <p className="text-xl font-bold text-gray-900">
                            {typeof safeGet(graphData, 'summary.like', 0) === 'number'
                              ? safeGet(graphData, 'summary.like', 0).toLocaleString()
                              : safeGet(graphData, 'summary.like', 0)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center border border-gray-100">
                          {commentsIcon}
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Comments</p>
                          <p className="text-xl font-bold text-gray-900">
                            {typeof safeGet(graphData, 'summary.comment', 0) === 'number'
                              ? safeGet(graphData, 'summary.comment', 0).toLocaleString()
                              : safeGet(graphData, 'summary.comment', 0)}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center border border-gray-100">
                          {shareIcon}
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Shares</p>
                          <p className="text-xl font-bold text-gray-900">
                            {typeof safeGet(graphData, 'summary.share', 0) === 'number'
                              ? safeGet(graphData, 'summary.share', 0).toLocaleString()
                              : safeGet(graphData, 'summary.share', 0)}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="campaignsChartTop mt-6">
                    <div className="campaignsChartTop__col">
                      <div className="campaignsChartTop__col-row">
                        <div className="campaignsChartTop__reach bg-white border border-gray-200 rounded-xl p-5" style={{ boxShadow: "0 2px 8px -2px rgba(0, 0, 0, 0.06)" }}>
                          <div className="campaignsChartTop__reach-title">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Reach</p>
                            <h2 className="text-2xl font-bold text-gray-900">
                              {safeGet(graphData, 'reach.header', 0)}
                            </h2>
                          </div>
                          <div className="campaignsChartTop__reach-chart mt-4">
                            <LineChart
                              data={safeGet(graphData, 'reach.data', [])}
                              labels={graphData.label || []}
                            />
                          </div>
                        </div>
                        <div className="campaignsChartTop__reach bg-white border border-gray-200 rounded-xl p-5" style={{ boxShadow: "0 2px 8px -2px rgba(0, 0, 0, 0.06)" }}>
                          <div className="campaignsChartTop__reach-title">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Impressions</p>
                            <h2 className="text-2xl font-bold text-gray-900">
                              {safeGet(graphData, 'impression.header', 0)}
                            </h2>
                          </div>
                          <div className="campaignsChartTop__reach-chart mt-4">
                            <BarChart
                              labels={graphData.label || []}
                              data={safeGet(graphData, 'impression.data', [])}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="campaignsChartTop__col-total bg-white border border-gray-200 rounded-xl p-5 mt-6" style={{ boxShadow: "0 2px 8px -2px rgba(0, 0, 0, 0.06)" }}>
                        <div className="campaignsChartTop__col-total-title">
                          <p className="text-sm font-semibold text-gray-800 mb-3">Impressions vs Reach</p>
                          <div className="campaignsChartTop__col-total-title-inner flex gap-4">
                            <div className="campaignsChartTop__col-total-title-item flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full" style={{ background: "#08A708" }}></span>
                              <span className="text-xs text-gray-600">
                                Total Impressions - {safeGet(graphData, 'impression.header', 0)}
                              </span>
                            </div>
                            <div className="campaignsChartTop__col-total-title-item flex items-center gap-2">
                              <span className="w-3 h-3 rounded-full" style={{ background: "#1F4BE7" }}></span>
                              <span className="text-xs text-gray-600">
                                Total Reach - {safeGet(graphData, 'reach.header', 0)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="campaignsChartTop__col-total-chart mt-4">
                          <MultipleLineChart
                            data1={safeGet(graphData, 'impression.data', [])}
                            data2={safeGet(graphData, 'reach.data', [])}
                            labels={graphData.label || []}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="campaignsChartTop__col bg-white border border-gray-200 rounded-xl overflow-hidden" style={{ boxShadow: "0 2px 8px -2px rgba(0, 0, 0, 0.06)" }}>
                      <div className="campaignsChartTop__age p-5 border-b border-gray-100">
                        <div className="campaignsChartTop__age-title">
                          <p className="text-sm font-semibold text-gray-800 mb-3">Average by Age</p>
                        </div>
                        <AgeChart
                          ageData={safeGet(graphData, 'ageData.data', [])}
                        />
                      </div>
                      <div className="campaignsChartTop__gender p-5">
                        <div className="campaignsChartTop__gender-title mb-4">
                          <p className="text-sm font-semibold text-gray-800">Audience by Gender</p>
                          <span className="text-xs text-gray-500">Entire campaign</span>
                        </div>
                        <div className="campaignsChartTop__gender-row grid grid-cols-3 gap-4">
                          <div className="campaignsChartTop__gender-item bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Male</p>
                            <p className="text-xl font-bold text-gray-900 mt-1">
                              {safeGet(graphData, 'ageData.header.gender_m', 0)}%
                            </p>
                          </div>
                          <div className="campaignsChartTop__gender-item bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Female</p>
                            <p className="text-xl font-bold text-gray-900 mt-1">
                              {safeGet(graphData, 'ageData.header.gender_f', 0)}%
                            </p>
                          </div>
                          <div className="campaignsChartTop__gender-item bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Undisclosed</p>
                            <p className="text-xl font-bold text-gray-900 mt-1">
                              {safeGet(graphData, 'ageData.header.gender_u', 0)}%
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>

            {/* Charts Section */}
            <div className="campaignsChart">


              {/* Engagement Section */}
              <div
                className="bg-white rounded-2xl border border-gray-200 overflow-hidden"
                style={{ boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.08)" }}
              >
                {/* Subtle Top Accent */}
                <div
                  className="h-1"
                  style={{ background: "linear-gradient(90deg, rgb(94, 176, 90) 0%, rgb(54, 122, 64) 50%, rgb(16, 38, 32) 100%)" }}
                />

                <div className="p-6">
                  {/* Header Inside Card - Clickable for Accordion */}
                  <div
                    className="flex items-center justify-between cursor-pointer group"
                    onClick={() => toggleAccordion('engagement')}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform group-hover:scale-110"
                        style={{ background: "linear-gradient(135deg, rgb(94, 176, 90) 0%, rgb(54, 122, 64) 100%)" }}
                      >
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900 tracking-tight">Engagement</h3>
                        <p className="text-xs text-gray-500">Engagement rates and metrics</p>
                      </div>
                    </div>

                  </div>

                  <div className="mt-6 animate-in fade-in slide-in-from-top-2 duration-300">

                    {/* Rate Cards */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Amplification Rate</p>
                        <p className="text-2xl font-bold text-gray-900 mb-4">
                          {safeGet(graphData, 'ampRate.header', 0)}%
                        </p>
                        <div className="h-24">
                          <LineChart
                            percentage={true}
                            data={safeGet(graphData, 'ampRate.data', [])}
                            labels={graphData.label || []}
                          />
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Virality Rate</p>
                        <p className="text-2xl font-bold text-gray-900 mb-4">
                          {safeGet(graphData, 'viralityRate.header', 0)}%
                        </p>
                        <div className="h-24">
                          <LineChart
                            percentage={true}
                            data={safeGet(graphData, 'viralityRate.data', [])}
                            labels={graphData.label || []}
                          />
                        </div>
                      </div>
                      <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Engagement Rate</p>
                        <p className="text-2xl font-bold text-gray-900 mb-4">
                          {safeGet(graphData, 'engagementRate.header', 0)}%
                        </p>
                        <div className="h-24">
                          <LineChart
                            percentage={true}
                            data={safeGet(graphData, 'engagementRate.data', [])}
                            labels={graphData.label || []}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Total Engagement Chart */}
                    <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                      <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                        <p className="text-sm font-semibold text-gray-800">Total Engagement</p>
                        <div className="flex flex-wrap gap-4">
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full" style={{ background: "#08A708" }}></span>
                            <span className="text-xs text-gray-600">Likes</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full" style={{ background: "#1F4BE7" }}></span>
                            <span className="text-xs text-gray-600">Comments</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full" style={{ background: "#FF319A" }}></span>
                            <span className="text-xs text-gray-600">Shares</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="w-3 h-3 rounded-full" style={{ background: "#8D0048" }}></span>
                            <span className="text-xs text-gray-600">Interactions</span>
                          </div>
                        </div>
                      </div>
                      <div className="campaignsChartBot__total-chart">
                        <MultipleLineChart
                          data1={safeGet(graphData, 'like.data', [])}
                          data2={safeGet(graphData, 'comment.data', [])}
                          data3={safeGet(graphData, 'share.data', [])}
                          data4={safeGet(graphData, 'interaction.data', [])}
                          labels={graphData.label || []}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : isGraphDataLoaded ? (
          <div className="flex items-center justify-center py-12">
            <p className="text-gray-500 text-lg">
              {statsError === "No response found" ? "No response found" : "No insights data available for this report"}
            </p>
          </div>
        ) : null}
      </div>
      <Toast list={toastList} setList={setToastList} />
    </div>
  );
}
